/**
 * Distance-keyed hazard tape. Spawn no longer depends on dt / vehicle speed,
 * so two players with the same seed + difficulty + stage get the same road
 * (first to crash = shorter distance).
 */
import { PLAYER_Z } from "./constants";
import type { DifficultyDef } from "./difficulty";
import type { StageDef } from "./stages";
import type { HazardKind } from "./collide";
import { hashSeed, mulberry32 } from "./rng";

/** World Z gap from player to a freshly spawned wave (matches old z≈-58). */
export const TRACK_LOOKAHEAD = PLAYER_Z + 58;

export type TrackWave = {
  d: number;
  hazards: { lane: number; kind: HazardKind }[];
  coinLane: number | null;
  jumpLane: number | null;
  hazardZJitter: number;
  jumpZJitter: number;
};

export function speedAtDistance(diff: DifficultyDef, distance: number) {
  const chunks = Math.floor(Math.max(0, distance) / diff.chunkMeters);
  return Math.min(diff.maxSpeed, diff.baseSpeed + chunks * diff.speedPerChunk);
}

export function waveGap(diff: DifficultyDef, distance: number) {
  const speed = speedAtDistance(diff, distance);
  const span = Math.max(0.05, diff.maxSpeed - diff.baseSpeed);
  const t = Math.min(1, (speed - diff.baseSpeed) / span);
  const interval = Math.max(
    0.35,
    diff.hazardSpawnMin - t * (diff.hazardSpawnMin - 0.38),
  );
  return Math.max(9, interval * speed);
}

function pickSpawnLanes(
  rand: () => number,
  doubleBias: number,
  speed: number,
): number[] {
  const haste = speed > 22 ? 0.12 : 0;
  const singleCut = Math.max(0.2, 1 - doubleBias - haste);
  if (rand() < singleCut) {
    return [Math.floor(rand() * 3)];
  }
  const a = Math.floor(rand() * 3);
  let b = Math.floor(rand() * 3);
  while (b === a) b = Math.floor(rand() * 3);
  return [a, b];
}

export function makeWave(
  seed: string,
  index: number,
  diff: DifficultyDef,
  stage: StageDef,
  atDistance: number,
): TrackWave {
  const rand = mulberry32(hashSeed(seed, index + 1));
  const speed = speedAtDistance(diff, atDistance);
  const lanes = pickSpawnLanes(rand, diff.doubleLaneBias, speed);
  let trafficUsed = false;
  const hazards: TrackWave["hazards"] = [];
  for (const lane of lanes) {
    let kind: HazardKind =
      rand() < diff.rivalSpawnChance
        ? rand() < 0.45
          ? "truck"
          : "rival"
        : rand() < stage.holeChance
          ? "hole"
          : rand() < 0.4
            ? "barrier"
            : "box";
    if ((kind === "rival" || kind === "truck") && trafficUsed && diff.id === "hard") {
      kind =
        rand() < stage.holeChance ? "hole" : rand() < 0.5 ? "barrier" : "box";
    }
    if (kind === "rival" || kind === "truck") trafficUsed = true;
    hazards.push({ lane, kind });
  }
  const leftover = [0, 1, 2].filter((l) => !lanes.includes(l));
  let coinLane: number | null = null;
  let jumpLane: number | null = null;
  if (leftover.length && rand() < diff.coinSpawnChance) {
    const i = Math.floor(rand() * leftover.length);
    coinLane = leftover.splice(i, 1)[0]!;
  }
  if (leftover.length && rand() < diff.jumpSpawnChance) {
    jumpLane = leftover[Math.floor(rand() * leftover.length)]!;
  }
  return {
    d: atDistance,
    hazards,
    coinLane,
    jumpLane,
    hazardZJitter: rand() * 4,
    jumpZJitter: rand() * 3,
  };
}

export const FIRST_WAVE_AT = 12;
